import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Menu, Table } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { CountBadge, EmptyState, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { EOrderDirection, EOrderField } from 'common/const/common.enum';
import { DEFAULT_LIST_OFFSET, DEFAULT_PAGINATION_PAGE, LIST_LIMIT_20, PREVIOUS_PATH_KEY } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ISupplyListPayload } from 'entities/Supply/Supply.models';
import { ESupplyGroupType, ESupplyStatus } from 'entities/Supply/Supply.enum';
import { mapSupplyListToDataSource, renderSupplyListRecords } from 'entities/Supply/Supply.helper';
import {
  getSupplyList,
  getSupplyListChunk,
  selectIsSupplyLoading,
  selectSupplyCount,
  selectSupplyList,
  setSupplyList,
} from 'entities/Supply/Supply.slice';
import { selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
}

export const SupplyListTabForUser: FC<IComponentProps> = ({ header }) => {
  const dispatch = useAppDispatch();
  // supply
  const supplyList = useAppSelector(selectSupplyList);
  const supplyCount = useAppSelector(selectSupplyCount);
  const isSupplyLoading = useAppSelector(selectIsSupplyLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [payload, setPayload] = useState<ISupplyListPayload>({
    userId: currentUser?.id,
    statuses: [ESupplyStatus.Approved, ESupplyStatus.Delivery],
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
    orderField: EOrderField.DeliveryDate,
    orderDirection: EOrderDirection.ASC,
  });
  const location = useLocation();
  const navigate = useNavigate();

  const dataSource = useMemo(() => mapSupplyListToDataSource(supplyList), [supplyList]);

  const navigateToRequest = (path: string) => {
    sessionStorage.setItem(PREVIOUS_PATH_KEY, `${location.pathname}${location.search}`);
    navigate(path);
  };

  const columns = renderSupplyListRecords(currentUser?.account, navigateToRequest);

  const onSearchClick = async (value?: string) => {
    const newPayload = {
      ...payload,
      search: value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    await dispatch(getSupplyList(newPayload));
  };

  useEffect(() => {
    const fetch = async () => {
      dispatch(setSupplyList([]));
      await dispatch(getSupplyList(payload));
      setFetchLoading(false);
    };

    fetch();
  }, []);

  return (
    <InfiniteScroll
      loading={scrollLoading}
      hasMore={!isSupplyLoading && supplyList.length < supplyCount}
      loader={<Spin />}
      scrollToTopTrigger={[payload]}
      loadMore={async () => {
        setScrollLoading(true);
        setPage(page + 1);
        await dispatch(
          getSupplyListChunk({
            ...payload,
            offset: LIST_LIMIT_20 * page,
          }),
        );
        setScrollLoading(false);
      }}
    >
      {header}

      <Menu
        items={[
          {
            key: ESupplyStatus.Approved,
            label: (
              <>
                Ожидаемые поставки
                <CountBadge count={supplyCount} />
              </>
            ),
          },
        ]}
        selectedKeys={[ESupplyStatus.Approved]}
        mode="horizontal"
      />

      <div className="supply-list__filters mb-7">
        <SearchInput placeholder="Поиск" onSearch={onSearchClick} />
      </div>

      <Table
        className={`supply-list__table ${!!supplyList.length && supplyList.length === supplyCount && 'table-end-of-list'} ${!supplyList.length ? 'table-empty' : ''}`}
        showHeader={false}
        pagination={false}
        dataSource={dataSource}
        columns={columns}
        components={{
          body: {
            row: ({ children, ...props }: any) => {
              const row = props['data-row-key'];
              const record = dataSource.find((item) => item.key === row);

              if (record?.type === ESupplyGroupType.Date) {
                return (
                  <tr className="supply-list__table-date-row">
                    <td colSpan={columns.length}>
                      <span className="text-controls-accent">{record.label}</span>
                    </td>
                  </tr>
                );
              }

              return <tr {...props}>{children}</tr>;
            },
          },
        }}
        locale={{
          emptyText: fetchLoading ? (
            <Spin />
          ) : (
            <EmptyState
              title="В этом разделе нет поставок"
              description="Они появятся здесь после назначения даты доставки на стороне поставщика."
            />
          ),
        }}
      />
    </InfiniteScroll>
  );
};
